/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserPreferenceService } from '../shared/user-preference.service';

/** The tabs of the favourites screen, and the `where` clause each one needs. */
export type FavoriteKind = 'all' | 'file' | 'folder' | 'site';

const WHERE: Record<FavoriteKind, string | null> = {
  all: null,
  file: '(EXISTS(target/file))',
  folder: '(EXISTS(target/folder))',
  site: '(EXISTS(target/site))'
};

/** Preference key holding the pinned favourites, as a comma-separated id list. */
const PINNED_PREFERENCE = 'aca.favorites.pinned';

export interface FavoriteRow {
  id: string;
  name: string;
  title: string;
  kind: Exclude<FavoriteKind, 'all'>;
  /** Repository path of the containing folder, when the API returned one. */
  location: string;
  modifiedAt: string;
  modifiedByName: string;
  /** When this item was added to favourites. */
  favoritedAt: string;
  documentType: string;
  status: string;
  pinned: boolean;
}

export interface FavoriteCounts {
  all: number;
  file: number;
  folder: number;
  site: number;
}

export type FavoriteSort = 'recent' | 'name' | 'modified';

/**
 * The favourites screen's data (UI-02).
 *
 * Favourites themselves are Alfresco's own — `/people/-me-/favorites` — so
 * everything on the screen is repository truth, including the per-tab counts,
 * which come from the API's own totals rather than from counting loaded rows.
 *
 * Pinning has no equivalent in Alfresco, so it is stored as a **server-side user
 * preference** rather than invented in the browser; see {@link PINNED_PREFERENCE}.
 */
@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private readonly api = inject(AlfrescoApiService);
  private readonly preferences = inject(UserPreferenceService);

  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  // ---- favourites ---------------------------------------------------------

  /** One page of favourites for a tab, with the pinned ones marked. */
  list(kind: FavoriteKind, skipCount = 0, maxItems = 100): Observable<{ rows: FavoriteRow[]; total: number }> {
    const query: any = { skipCount, maxItems, include: 'properties,path,allowableOperations' };
    const where = WHERE[kind];
    if (where) {
      query.where = where;
    }

    return forkJoin({
      response: this.call('/people/-me-/favorites', 'GET', null, query),
      pinned: this.pinned()
    }).pipe(
      map(({ response, pinned }) => ({
        rows: (response?.list?.entries ?? []).map((e: any) => this.toRow(e.entry, pinned)),
        total: response?.list?.pagination?.totalItems ?? 0
      }))
    );
  }

  /** Per-tab totals, taken from the API's own pagination rather than counted locally. */
  counts(): Observable<FavoriteCounts> {
    const total = (kind: FavoriteKind) => {
      const query: any = { maxItems: 1 };
      const where = WHERE[kind];
      if (where) {
        query.where = where;
      }
      return this.call('/people/-me-/favorites', 'GET', null, query).pipe(
        map((res) => res?.list?.pagination?.totalItems ?? 0),
        catchError(() => of(0))
      );
    };

    return forkJoin({ all: total('all'), file: total('file'), folder: total('folder'), site: total('site') });
  }

  /** Adds a node to favourites. */
  add(nodeId: string, isFolder: boolean): Observable<any> {
    const target = isFolder ? { folder: { guid: nodeId } } : { file: { guid: nodeId } };
    return this.call('/people/-me-/favorites', 'POST', { target });
  }

  /** Removes a node from favourites, and drops any pin it had. */
  remove(nodeId: string): Observable<any> {
    return this.call(`/people/-me-/favorites/${nodeId}`, 'DELETE').pipe(switchMap(() => this.setPinned(nodeId, false)));
  }

  // ---- pinning ------------------------------------------------------------

  /** Node ids the user has pinned. */
  pinned(): Observable<Set<string>> {
    return this.preferences.read<string>(PINNED_PREFERENCE).pipe(
      map(
        (raw) =>
          new Set<string>(
            String(raw ?? '')
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
          )
      )
    );
  }

  /** Pins or unpins a node, persisting the whole set back to the user's preferences. */
  setPinned(nodeId: string, pinned: boolean): Observable<Set<string>> {
    return this.pinned().pipe(
      switchMap((current) => {
        const next = new Set(current);
        if (pinned) {
          next.add(nodeId);
        } else {
          next.delete(nodeId);
        }
        return this.preferences.write(PINNED_PREFERENCE, [...next].join(',')).pipe(map(() => next));
      })
    );
  }

  // ---- mapping ------------------------------------------------------------

  private toRow(entry: any, pinned: Set<string>): FavoriteRow {
    const target = entry?.target ?? {};
    const kind: Exclude<FavoriteKind, 'all'> = target.file ? 'file' : target.folder ? 'folder' : 'site';
    const node = target.file ?? target.folder ?? target.site ?? {};
    const props = entry?.properties ?? {};
    const id = entry?.targetGuid ?? node.guid ?? node.id;

    return {
      id,
      name: node.name || node.title || node.id || '',
      title: props['cm:title'] ?? '',
      kind,
      location: this.locationOf(node),
      modifiedAt: node.modifiedAt ?? entry?.createdAt ?? '',
      modifiedByName: node.modifiedByUser?.displayName ?? node.modifiedByUser?.id ?? '',
      favoritedAt: entry?.createdAt ?? '',
      documentType: props['dms:documentType'] ?? '',
      status: props['dms:status'] ?? '',
      pinned: pinned.has(id)
    };
  }

  /** Human-readable containing folder, with the repository root trimmed off. */
  private locationOf(node: any): string {
    const path: string = node?.path?.name ?? '';
    if (!path) {
      return '';
    }
    return path.replace(/^\/Company Home\/?/, '').replace(/\//g, ' / ') || 'Company Home';
  }
}
